import moment from 'moment';

// ANGULAR
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Component, OnInit, Inject, signal, inject } from '@angular/core';

// ANGULAR MATERIAL
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

// SERICES
import { NgFormBuilderHelperService } from 'src/app/app-services-helper/ng-form-builder-helper.service';

// RxJS 6
import { debounceTime, Subscription, tap } from 'rxjs';

// MODELS
import {
  WHScheduleAllWorkStepPayload,
  WHIconENUM,
  WHToastService,
} from '@workheld/workheld-shared-lib';
import { isFuture } from 'date-fns';
import { RemindTechnicianService } from 'src/app/app-services/remind-technician.service';
import {
  notificationTimeFunction,
  updateAlertOptions} from 'src/app/app-shared/functions/dialogs/remind-technician';
import { UIService } from 'src/app/app-services/ui.service';
import { dynamicAnimation } from 'src/app/app-shared/helpers/animations';

@Component({
  selector: 'ng-bee-mat-dialog-work-step-scheduler-entry',
  templateUrl: './mat-dialog-work-step-scheduler-entry.component.html',
  styleUrls: ['./mat-dialog-work-step-scheduler-entry.component.scss'],
  animations: [dynamicAnimation],
  standalone: false})
export class MatDialogWorkStepSchedulerEntryComponent implements OnInit {
  // DATA VAR
  public scheduleFromGroup: FormGroup;

  remindTechnicianFormGroup = new FormGroup({
    id: new FormControl(null),
    reminderOffsetInMinutes: new FormControl(null, Validators.required),
    reminderComment: new FormControl('')});

  private remindTechnicianService = inject(RemindTechnicianService);
  private ngxToastrService = inject(WHToastService);
  alertOptions = this.remindTechnicianService.alertOptions();
  notificationTimeInfo = signal('');
  currentLocale = signal('en');

  // Existing notification data from workOrderDOM
  private existingReminderOffset: number | null = null;
  private existingReminderComment: string = '';

  // DATE VALUES
  public tempFormGroup = new FormGroup({
    dateSlotForm: new FormControl(),
    timeSlotForm: new FormControl(),
    enddateSlotForm: new FormControl(),
    endtimeSlotForm: new FormControl()});

  // MANAGE SUBSCRIPTIONS
  private subscriptions: Subscription[] = [];

  constructor(
    private ngFormBuilderHelperService: NgFormBuilderHelperService,
    public matDialogRef: MatDialogRef<MatDialogWorkStepSchedulerEntryComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      workOrderId: string;
      workOrderDOM;
      hasPastStartDate: boolean;
    },
    private uiService: UIService,
  ) {
    this.currentLocale = this.uiService.$currentLocale;
  }

  public ngOnInit(): void {
    this.scheduleFromGroup =
      this.ngFormBuilderHelperService.initScheduleDateFormGroup();
    this.scheduleFromGroup.controls['workOrderId'].patchValue(
      this.data.workOrderId,
    );

    // Prefill with existing notification data from workOrderDOM
    this.prefillExistingNotification();

    this.subscriptions.push(
      this.tempFormGroup.valueChanges
        .pipe(
          debounceTime(200),
          tap((value) => {
            this.updateRemindTechnicianFormState();
          }),
        )
        .subscribe(),
    );

    this.subscriptions.push(
      this.tempFormGroup.controls['dateSlotForm'].valueChanges.subscribe(
        (value: string) => {
          let time = this.tempFormGroup.controls['timeSlotForm'].value;
          if (!time && value) {
            time = '00:00';
            this.tempFormGroup.controls['timeSlotForm'].patchValue(time);
          }
          const finalVersion = time
            ? moment(value)
                .set({ hour: time.slice(0, 2), minute: time.slice(-2) })
                .format()
            : value
              ? moment(value).format()
              : null;
          this.scheduleFromGroup.controls['startDate'].patchValue(finalVersion);

          this.alertOptions = updateAlertOptions(
            this.remindTechnicianService.alertOptions(),
            this.tempFormGroup.controls['dateSlotForm']?.value,
            this.tempFormGroup.controls['timeSlotForm']?.value,
          );
        },
      ),
    );

    this.subscriptions.push(
      this.tempFormGroup.controls['timeSlotForm'].valueChanges.subscribe(
        (value: any) => {
          const date = this.tempFormGroup.controls['dateSlotForm'].value;
          if (!date) {
            this.tempFormGroup.controls['dateSlotForm'].patchValue(
              moment().format(),
            );
          }
          const finalVersion = date
            ? moment(date)
                .set({ hour: value.slice(0, 2), minute: value.slice(-2) })
                .format()
            : moment(date).format();
          this.scheduleFromGroup.controls['startDate'].patchValue(finalVersion);

          this.alertOptions = updateAlertOptions(
            this.remindTechnicianService.alertOptions(),
            this.tempFormGroup.controls['dateSlotForm']?.value,
            this.tempFormGroup.controls['timeSlotForm']?.value,
          );
        },
      ),
    );

    this.subscriptions.push(
      this.tempFormGroup.controls['enddateSlotForm'].valueChanges.subscribe(
        (value: string) => {
          let time = this.tempFormGroup.controls['endtimeSlotForm'].value;
          if (!time && value) {
            time = '23:59';
            this.tempFormGroup.controls['endtimeSlotForm'].patchValue(time);
          }
          const finalVersion = time
            ? moment(value)
                .set({ hour: time.slice(0, 2), minute: time.slice(-2) })
                .format()
            : value
              ? moment(value).format()
              : null;
          this.scheduleFromGroup.controls['endDate'].patchValue(finalVersion);

          // Selecting end date confirms the user is done with date setup
          this.validateSelectedReminderOption();
        },
      ),
    );

    this.subscriptions.push(
      this.remindTechnicianFormGroup
        .get('reminderOffsetInMinutes')
        ?.valueChanges.pipe(debounceTime(500))
        .subscribe((value) => {
          this.notificationTimeInfo.set(
            notificationTimeFunction(
              this.remindTechnicianFormGroup.controls['reminderOffsetInMinutes']
                ?.value,
              this.tempFormGroup.controls['dateSlotForm']?.value,
              this.tempFormGroup.controls['timeSlotForm']?.value,
            ),
          );
        }),
    );

    this.subscriptions.push(
      this.tempFormGroup.controls['endtimeSlotForm'].valueChanges.subscribe(
        (value: any) => {
          const date = this.tempFormGroup.controls['enddateSlotForm'].value;
          if (!date) {
            this.tempFormGroup.controls['enddateSlotForm'].patchValue(
              moment().format(),
            );
          }
          const finalVersion = date
            ? moment(date)
                .set({ hour: value.slice(0, 2), minute: value.slice(-2) })
                .format()
            : moment(date).format();
          this.scheduleFromGroup.controls['endDate'].patchValue(finalVersion);

          // Changing end time also confirms the schedule
          this.validateSelectedReminderOption();
        },
      ),
    );
  }

  public get notificationTime(): string {
    return notificationTimeFunction(
      this.remindTechnicianFormGroup.get('reminderOffsetInMinutes')?.value,
      this.tempFormGroup.controls['dateSlotForm']?.value,
      this.tempFormGroup.controls['timeSlotForm']?.value,
    );
  }

  public handleSubmit(): void {
    const schedulePayload: WHScheduleAllWorkStepPayload =
      this.scheduleFromGroup.value;

    const remindTechnicianPayload = this.remindTechnicianFormGroup.value;
    remindTechnicianPayload.id = this.data.workOrderId;

    this.matDialogRef.close({
      schedulePayload,
      remindTechnicianPayload});
  }

  private prefillExistingNotification(): void {
    const workOrderDOM = this.data.workOrderDOM;
    if (workOrderDOM?.reminderOffsetInMinutes != null) {
      this.existingReminderOffset = workOrderDOM.reminderOffsetInMinutes;
      this.existingReminderComment = workOrderDOM.reminderComment || '';
      this.remindTechnicianFormGroup.patchValue({
        reminderOffsetInMinutes: this.existingReminderOffset,
        reminderComment: this.existingReminderComment});
    }
  }

  private validateSelectedReminderOption(): void {
    const startDate = this.tempFormGroup.controls['dateSlotForm']?.value;
    const startTime = this.tempFormGroup.controls['timeSlotForm']?.value;
    if (!startDate || !startTime) {
      return;
    }
    const currentValue = this.remindTechnicianFormGroup.get(
      'reminderOffsetInMinutes',
    )?.value;
    if (currentValue == null) {
      return;
    }
    const isStillAvailable = this.alertOptions.some(
      (option) => option.value === currentValue,
    );
    if (!isStillAvailable) {
      this.remindTechnicianFormGroup.get('reminderOffsetInMinutes')?.reset();
      this.notificationTimeInfo.set('');
      // Forget the prefilled value so it won't be re-applied or re-validated
      this.existingReminderOffset = null;
      this.existingReminderComment = '';
      this.ngxToastrService.warning('technicianNotification.toast.selectedIntervalNoLongerAvailable.message');
    }
  }

  private updateRemindTechnicianFormState() {
    if (
      this.data.workOrderDOM?.calculatedWorkOrderStatusId === 0 &&
      this.scheduleFromGroup.controls['startDate'].value &&
      isFuture(this.scheduleFromGroup.controls['startDate'].value) &&
      (this.scheduleFromGroup.controls['mode'].value === 'ALL' ||
        !this.data.hasPastStartDate)
    ) {
      this.remindTechnicianFormGroup.enable();
      // Re-apply existing notification values if form was just re-enabled and has no selection
      if (
        this.existingReminderOffset != null &&
        this.remindTechnicianFormGroup.get('reminderOffsetInMinutes')?.value ==
          null
      ) {
        this.remindTechnicianFormGroup.patchValue({
          reminderOffsetInMinutes: this.existingReminderOffset,
          reminderComment: this.existingReminderComment});
      }
    } else {
      this.remindTechnicianFormGroup.disable();
      // Preserve existing notification values visually (disabled but showing preselected value)
      this.remindTechnicianFormGroup.patchValue({
        id: null,
        reminderOffsetInMinutes: this.existingReminderOffset,
        reminderComment: this.existingReminderComment});
      this.remindTechnicianFormGroup.reset();
    }
  }

  public closeDialog(): void {
    this.matDialogRef.close();
  }

  public toggleScheduleMode($event: MatSlideToggleChange) {
    if ($event.checked) {
      this.scheduleFromGroup.controls['mode'].patchValue('ONLY_UNSCHEDULED');
    } else {
      this.scheduleFromGroup.controls['mode'].patchValue('ALL');
    }
    this.updateRemindTechnicianFormState();
  }

  public get startDateFormControl(): FormControl {
    return this.scheduleFromGroup.controls['startDate'] as FormControl;
  }

  public get endDateFormControl(): FormControl {
    return this.scheduleFromGroup.controls['endDate'] as FormControl;
  }

  public get calendarIcon(): string {
    return WHIconENUM.CalendarIcon as string;
  }

  public get saveIcon(): string {
    return WHIconENUM.SaveIcon as string;
  }

  public get cancelIcon(): string {
    return WHIconENUM.CancelIcon as string;
  }

  public get dateSlotForm(): string {
    return this.tempFormGroup.controls['dateSlotForm'].value as string;
  }

  public get timeSlotForm(): string {
    return this.tempFormGroup.controls['timeSlotForm'].value as string;
  }

  public get enddateSlotForm(): string {
    return this.tempFormGroup.controls['enddateSlotForm'].value as string;
  }

  public get endtimeSlotForm(): string {
    return this.tempFormGroup.controls['endtimeSlotForm'].value as string;
  }

  public get dateSet(): boolean {
    return !!this.dateSlotForm || !!this.enddateSlotForm;
  }
}
